
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { ChevronDown, Check, Search } from 'lucide-react';

const TOATE_BAROURILE = [
  "Alba", "Arad", "Argeș", "Bacău", "Bihor", "Bistrița-Năsăud", "Botoșani", "Brașov", "Brăila", "București",
  "Buzău", "Caraș-Severin", "Călărași", "Cluj", "Constanța", "Covasna", "Dâmbovița", "Dolj", "Galați", "Giurgiu",
  "Gorj", "Harghita", "Hunedoara", "Ialomița", "Iași", "Ilfov", "Maramureș", "Mehedinți", "Mureș", "Neamț",
  "Olt", "Prahova", "Satu Mare", "Sălaj", "Sibiu", "Suceava", "Teleorman", "Timiș", "Tulcea", "Vaslui",
  "Vâlcea", "Vrancea", "Alte Barouri (Străinătate)"
];

const RegisterView: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', bar: '', barCardNumber: '' });
  
  // Stare pentru dropdown-ul custom
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Închide dropdown-ul când dai click în afară
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredBars = TOATE_BAROURILE.filter(bar => 
    bar.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.bar) {
      setError("Vă rugăm să selectați baroul dumneavoastră.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Parolele nu coincid.");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Pasul 1: Crearea contului în Auth cu metadata inclus
      // Trigger-ul SQL (on_auth_user_created) va crea automat rândul în 'profiles' pe baza acestor metadata.
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email.toLowerCase().trim(),
        password: form.password,
        options: {
          data: { 
            name: form.name, 
            bar: form.bar,
            barCardNumber: form.barCardNumber,
            isApproved: false // Pentru flow-ul de Beta
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        // Verificăm dacă email confirmation este necesar
        if (!authData.session) {
          setSuccess("✓ Cont creat cu succes! Pentru a accesa platforma, verifică email-ul și dă click pe link-ul de confirmare.");
          setError(null);
          setForm({ name: '', email: '', password: '', confirmPassword: '', bar: '', barCardNumber: '' });
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      console.error("Registration error:", err);
      
      if (err.message?.includes('violates row-level security policy')) {
        setError("Profilul va fi creat automat la prima autentificare. Verificați email-ul.");
      } else if (err.message?.includes('already registered') || err.message?.includes('already been registered')) {
        setError("Această adresă de email este deja înregistrată.");
      } else if (err.message?.includes('Password should be')) {
        setError("Parola trebuie să aibă minim 6 caractere.");
      } else if (err.message?.includes('rate limit') || err.status === 429) {
        setError("Prea multe încercări. Vă rugăm așteptați câteva minute.");
      } else {
        setError("Eroare la crearea contului. Vă rugăm încercați din nou.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 md:p-8 relative overflow-hidden">
      {/* Background Aurora */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-indigo-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[60%] h-[60%] bg-blue-600/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-0 items-stretch relative z-10 animate-in zoom-in-95 duration-1000 shadow-[0_64px_128px_-32px_rgba(0,0,0,0.8)] rounded-[4rem] border border-white/10">
        
        {/* Info Side (Blue Gradient) */}
        <div className="hidden lg:flex flex-col justify-center p-16 bg-gradient-to-br from-indigo-600 to-indigo-800 text-white relative overflow-hidden rounded-l-[4rem]">
          <div className="relative z-10">
            <span className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-200 mb-8 inline-block opacity-70 italic">Avocatură Digitală 2.0</span>
            <h2 className="text-6xl font-black italic tracking-tighter leading-[0.9] mb-10">
              Cabinetul <br/> <span className="text-white/40">Viitorului</span>
            </h2>
            <div className="space-y-6">
              {[
                "Monitorizare Automată Portal.just.ro",
                "Analiză Documente cu Gemini AI",
                "Arhivă Cloud Securizată",
                "Calendar Inteligent de Termene"
              ].map(f => (
                <div key={f} className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest italic text-indigo-100">
                  <div className="w-6 h-6 bg-white/10 rounded-lg flex items-center justify-center text-indigo-200 text-xs">✓</div>
                  {f}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute -bottom-10 -right-10 text-[200px] font-black italic opacity-10 leading-none select-none">LEX</div>
        </div>

        {/* Form Side (Dark Glass) */}
        <div className="bg-[#020617]/90 backdrop-blur-3xl p-10 md:p-14 flex flex-col relative rounded-[4rem] lg:rounded-l-none">
          <div className="mb-10">
            <h3 className="text-4xl font-black text-white italic tracking-tighter">Creează Cont</h3>
            <p className="text-indigo-400 text-[10px] font-black uppercase tracking-widest mt-2 italic opacity-80">Acces Closed Beta • Prin Invitație • 100% Gratuit</p>
          </div>

          {success && (
            <div className="mb-8 p-6 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-[2rem] text-[9px] font-black uppercase italic tracking-tight text-center flex items-center justify-center gap-3">
              <span className="text-emerald-400 text-base">✅</span> {success}
            </div>
          )}

          {error && (
            <div className="mb-8 p-6 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-[2rem] text-[9px] font-black uppercase italic tracking-tight text-center animate-shake flex items-center justify-center gap-3">
              <span className="text-amber-400 text-base">⚠️</span> {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-6 flex-grow">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Nume Complet</label>
                <input 
                  required 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 text-xs placeholder:text-slate-700 transition-all" 
                  placeholder="Av. Popescu" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Email</label>
                <input 
                  type="email" 
                  required 
                  value={form.email} 
                  onChange={e => setForm({...form, email: e.target.value})} 
                  className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 text-xs placeholder:text-slate-700 transition-all" 
                  placeholder="email@avocat.ro" 
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Parolă de Acces</label>
              <input 
                type="password" 
                required 
                value={form.password} 
                onChange={e => setForm({...form, password: e.target.value})} 
                className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 text-xs placeholder:text-slate-700 transition-all" 
                placeholder="Minim 6 caractere" 
              />
            </div>

            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Confirmă Parola</label>
              <input 
                type="password" 
                required 
                value={form.confirmPassword} 
                onChange={e => setForm({...form, confirmPassword: e.target.value})} 
                className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 text-xs placeholder:text-slate-700 transition-all" 
                placeholder="Confirmă parola" 
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* BAROURI CUSTOM DROPDOWN */}
              <div className="space-y-2" ref={dropdownRef}>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Baroul de Apartenență</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`w-full p-5 bg-[#0f172a] border ${form.bar ? 'border-indigo-500/50' : 'border-white/5'} rounded-2xl outline-none font-black text-[10px] text-left text-white focus:border-indigo-500 uppercase tracking-widest transition-all cursor-pointer flex items-center justify-between shadow-inner italic hover:bg-[#151e32]`}
                  >
                    <span className={form.bar ? "text-white" : "text-slate-700"}>
                      {form.bar || "SELECTAȚI BAROUL..."}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-indigo-500 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute bottom-full left-0 w-full mb-2 bg-[#0f172a] border border-indigo-500/30 rounded-2xl overflow-hidden z-50 shadow-2xl shadow-black/80">
                      {/* Search Input */}
                      <div className="p-3 border-b border-white/5 bg-[#080c1a]">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="CAUTĂ BAROU..."
                            className="w-full bg-[#1e293b] text-white text-[10px] font-bold py-2 pl-8 pr-3 rounded-lg outline-none border border-white/5 focus:border-indigo-500/50 uppercase tracking-wider placeholder:text-slate-600"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Options List */}
                      <div className="max-h-48 sm:max-h-56 overflow-y-auto custom-scrollbar p-1 overscroll-contain">
                        {filteredBars.length === 0 ? (
                          <div className="p-4 text-center text-[10px] text-slate-500 font-bold italic">NICIUN REZULTAT</div>
                        ) : (
                          filteredBars.map(bar => (
                            <div 
                              key={bar}
                              onClick={() => {
                                setForm({...form, bar: bar});
                                setIsDropdownOpen(false);
                                setSearchTerm('');
                              }}
                              className={`p-3 rounded-xl cursor-pointer flex items-center justify-between group transition-colors ${form.bar === bar ? 'bg-indigo-600' : 'hover:bg-white/5'}`}
                            >
                              <span className={`text-[10px] font-black uppercase tracking-widest italic ${form.bar === bar ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}>
                                {bar}
                              </span>
                              {form.bar === bar && <Check className="w-3 h-3 text-white" />}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Număr Legitimație</label>
                <input 
                  type="text" 
                  required 
                  value={form.barCardNumber} 
                  onChange={e => setForm({...form, barCardNumber: e.target.value})} 
                  className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 text-xs placeholder:text-slate-700 transition-all" 
                  placeholder="Ex: 1234/2010" 
                />
              </div>
            </div>

            <button 
              disabled={loading} 
              className="w-full py-6 bg-indigo-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.4em] shadow-2xl shadow-indigo-600/20 hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 mt-4"
            >
              {loading ? "SE LANSEAZĂ..." : "ÎNREGISTRARE CABINET →"}
            </button>
          </form>

          <button onClick={() => navigate('/login')} className="mt-10 text-[9px] font-black text-slate-600 uppercase tracking-widest hover:text-white transition-colors text-center w-full italic underline-offset-4 hover:underline">
            AI DEJA CONT? LOGIN
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterView;
